import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Box, Typography, Snackbar, Alert as MuiAlert } from '@mui/material';
import { useSelector } from 'react-redux';
import ProjectContentHandler from '../Components/ProjectContentHandler';
import ProjectOrthophotosHandler from '../Components/ProjectOrthophotosHandler';
import ProjectImportHandler from '../Components/ProjectImportHandler';
import ProjectPotreeLayersHandler from '../Components/ProjectPotreeLayersHandler';
import { projectsApi } from '../app/projects';
import { useParams } from 'react-router-dom';
import { layersApi } from '../app/layers';
import { selectCanDo } from '../app/auth';

const ProjectsEditPage = () => {
  const { id: projectId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [project, setProject] = useState(null);
  const [missions, setMissions] = useState([]);
  const [layers, setLayers] = useState([]);
  const canManage3DLayers = useSelector((state) =>
    selectCanDo(state)('manage3DLayers', { projectId })
  );

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const OUTPUTFIELDS = {
    project: `id,name,s3_folders,projectNumber,missions{id,name,inspections{id,name,missionId,views{id, spec},layers{type}},potreeLayers{id,name,metadataUrl,projectUrl}}`,
  };

  useEffect(() => {
    if (!projectId) return;

    projectsApi.get({ id: projectId }, OUTPUTFIELDS.project, ({ project }) => {
      if (project) {
        setProject(project);
        const m = project.missions || [];
        setMissions(m);
        const allIns = m.flatMap(m => m.inspections || []);
        allIns.forEach(ins => {
          layersApi.index(ins.id, (ls) => {
            ls.sort((a, b) => a.name.localeCompare(b.name));
            setLayers(prev => [...prev, ...ls]);
          });
        });
      }
    });
  }, [projectId]);

  const handleCloseSnackbar = (event, reason) => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%', px: 3, py: 4, color: 'white'}}>
      {/* Titre du projet */}
      {project && (
        <Box sx={{ maxWidth: 1200, margin: 'auto', padding: '1rem', color: 'white' }}>
          <Typography variant="h4" gutterBottom>Project Edition Page</Typography>
        </Box>
      )}

      <Box sx={{ maxWidth: 1200, margin: 'auto', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleChange} aria-label="Project tabs">
          <Tab label="Project content" />
          <Tab label="Orthophotos" />
          <Tab label="Import annotations" />
          {canManage3DLayers && <Tab label="Layers 3D" />}
        </Tabs>
      </Box>

      <Box mt={3}>
        {project && activeTab === 0 && (
          <ProjectContentHandler
            project={project}
            missions={missions}
            layers={layers}
            setMissions={setMissions}
            setLayers={setLayers}
            snackbar={snackbar}
            setSnackbar={setSnackbar}
            handleCloseSnackbar={handleCloseSnackbar}
          />
        )}

        {project && activeTab === 1 && (
          <ProjectOrthophotosHandler
            project={project}
            snackbar={snackbar}
            setSnackbar={setSnackbar}
            handleCloseSnackbar={handleCloseSnackbar}
          />
        )}

        {project && activeTab === 2 && (
          <ProjectImportHandler
            project={project}
            snackbar={snackbar}
            setSnackbar={setSnackbar}
            handleCloseSnackbar={handleCloseSnackbar}
          />
        )}

        {project && activeTab === 3 && canManage3DLayers && (
          <ProjectPotreeLayersHandler
            missions={missions}
            setMissions={setMissions}
            setSnackbar={setSnackbar}
          />
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <MuiAlert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            elevation={6}
            variant="filled"
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ProjectsEditPage;
